function Columns() {
    /**
     * Have all columns been resolved and translated?
     */
    this.initialized = false;

    /**
     * Many of the columns available to a user are dependent on the Akita account/customer.
     * - What integrations do they have?
     * - What properties does each integration have?
     * - What subscription level do they have?
     *
     * To address this, we can define the children of a column category as a function that returns a promise.
     *
     * Sometimes these promises must nest -- ex. What integrations? For each, whiich properties?
     *
     * This function 'resolves' these promises, recursively iterating through this object, resolving the promises
     * and replacing the function with the array that is returned by the function.
     */
    this.resolve = async function () {
        // Resolves array
        const promiseMap = (promiseList, functor) =>
            Promise.all(
                promiseList.map((promiseOrValue) =>
                    Promise.resolve(promiseOrValue).then(functor)
                )
            );

        // Resolves objects
        const promiseProps = (obj) =>
            Promise.all(
                Object.keys(obj).map((key) =>
                    Promise.resolve(obj[key]).then((val) => {
                        obj[key] = val;
                    })
                )
            ).then(() => obj);

        // Resolves recrusive deep objects
        const resolveNestedPromises = (obj) =>
            Promise.resolve(obj).then((obj) => {
                if (Array.isArray(obj)) {
                    return promiseMap(obj, (obj) => resolveNestedPromises(obj));
                } else if (obj && typeof obj === "object") {
                    // Object.assign(obj, obj.definition);
                    const obj2 = {};
                    for (const key in obj) {
                        obj2[key] = resolveNestedPromises(obj[key]);
                    }
                    return promiseProps(obj2);
                }
                return obj;
            });

        /**
         * Recursively resolve any promises in the this object
         */
        return await resolveNestedPromises(this);
    };

    this.translate = function ($i18n) {
        let prefix = "entityColumns.types";

        const traverse = function (obj) {
            for (var k in obj) {
                if (typeof obj[k] === "object" && obj[k] !== null) {
                    traverse(obj[k]);
                } else if (
                    /**
                     * label is a function [ call it ]
                     */
                    obj.hasOwnProperty(k) &&
                    typeof obj[k] == "function" &&
                    k == "label"
                ) {
                    obj["label"] = obj["label"]();
                } else if (
                    /**
                     * label is NOT a function use as translation
                     */
                    obj.hasOwnProperty(k) &&
                    typeof obj[k] != "function" &&
                    k == "label"
                ) {
                    obj["label"] = $i18n.t(prefix + "." + obj[k]) || obj[k];
                }
            }
        };
        traverse(this);
    };

    this.lookupByKey = function (haystack, needle, token) {
        var token = token || "key";
        const search = function (items, key) {
            let item;
            for (var index in items) {
                item = items[index];
                if (key == _.get(item, "definition." + token)) {
                    return item;
                }
                if (item.children) {
                    let result = search(item.children, key);
                    if (result) {
                        return result;
                    }
                }
            }
        };
        return search(haystack, needle);
    };

    this.init = async function ($i18n, $store, prefix) {
        this.label = "root";
        this.children = [
            // {
            //     definition: Object.assign({
            //         key: "isStarred",
            //         property: "orgs__is_starred",
            //     }),
            //     children: [],
            // },
            {
                definition: Object.assign({
                    key: "accountName",
                    label: "accountName",
                    property: "orgs__name",
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    key: "accountCreatedAt",
                    label: "accountCreatedAt",
                    property: "orgs__created_at",
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    key: "tags",
                    label: "tags",
                    property: "orgs__tags",
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    key: "taskLastComment",
                    label: "taskLastComment",
                    property: "orgs__last_task_comment",
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    label: "status",
                }),
                children: [
                    {
                        definition: Object.assign({
                            key: "isActive", // tracked
                            label: "isActive", // tracked
                            property: "orgs__is_active", // tracked
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "isChurned",
                            label: "isChurned",
                            property: "orgs__is_churned",
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "financials",
                }),
                children: [
                    {
                        definition: Object.assign({
                            key: $store.getters["usPreferences/VALUE_UNIT"]
                                ? $store.getters[
                                      "usPreferences/VALUE_UNIT"
                                  ].toLowerCase()
                                : "mrr",
                            label: $store.getters["usPreferences/VALUE_UNIT"]
                                ? $store.getters[
                                      "usPreferences/VALUE_UNIT"
                                  ].toLowerCase()
                                : "mrr",
                            property: "orgs__value",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "currency",
                            label: "currency",
                            property: "orgs__currency",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "customerStartDate",
                            label: "customerStartDate",
                            property: "orgs__customer_start_date",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "customerEndDate",
                            label: "customerEndDate",
                            property: "orgs__customer_end_date",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "contractStartDate",
                            label: "contractStartDate",
                            property: "orgs__contract_start_date",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "contractEndDate",
                            label: "contractEndDate",
                            property: "orgs__contract_end_date",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "activeLicenseCount",
                            label: "activeLicenseCount",
                            property: "orgs__active_license_count",
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "surveys",
                }),
                children: [
                    {
                        definition: Object.assign({
                            key: "nps",
                            label: "nps",
                            property: "orgs__nps",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "csats",
                            label: "csats",
                            property: "orgs__csats",
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "health",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "csmScore",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "csmScore",
                                    label: "csmScore",
                                    property: "orgs__csm_score",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "csmScoreLastUpdatedAt",
                                    label: "csmScoreLastUpdatedAt",
                                    property: "orgs__csm_score_last_updated_at",
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            key: "healthScore",
                            label: "healthScore",
                            property: "orgs__score",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "healthScorePercentage",
                            label: "healthScorePercentage",
                            property: "orgs__score_percentile",
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            label: "healthScoresCustom",
                        }),
                        children: axios
                            .get(window.akita.urls.api + "/v1/segments", {
                                params: {
                                    per_page: 100,
                                    q: [
                                        "entity_type_id[eq]=1",
                                        "type_id[eq]=3",
                                        "status_id[eq]=1",
                                    ],
                                },
                            })
                            .then(function (response) {
                                var items = [];
                                response.data.data.map((segment) => {
                                    items.push({
                                        definition: Object.assign({
                                            key: segment.name,
                                            label: () => {
                                                return segment.name;
                                            },
                                            property:
                                                "orgs__score_" +
                                                segment.id +
                                                "__score",
                                        }),
                                        children: [],
                                    });
                                    items.push({
                                        definition: Object.assign({
                                            key: segment.name + ": % Rank",
                                            label: () => {
                                                return (
                                                    segment.name + ": % Rank"
                                                );
                                            },
                                            property:
                                                "orgs__score_" +
                                                segment.id +
                                                "__score_percentile",
                                        }),
                                        children: [],
                                    });
                                });
                                return items;
                            }),
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "assignedUsers",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/roles", {
                        params: {
                            q: ["can_own_accounts[eq]=true"],
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((role) => {
                            items.push({
                                definition: Object.assign({
                                    key: role.display_name,
                                    label: () => {
                                        return role.display_name;
                                    },
                                    property:
                                        "orgs__role_" + role.id + "__full_name",
                                }),
                                children: [],
                            });
                            items.push({
                                definition: Object.assign({
                                    key: role.display_name + " (Email)",
                                    label: () => {
                                        return role.display_name + " (Email)";
                                    },
                                    property:
                                        "orgs__role_" + role.id + "__email",
                                }),
                                children: [],
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "usageMetrics",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "webSessions",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "webSessions_ever",
                                    label: "webSessions_ever",
                                    property:
                                        "orgs____sessions_organization__count",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_7",
                                    label: "webSessions_7",
                                    property:
                                        "orgs____sessions_organization__count_7d",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_7_delta_percentage",
                                    label: "webSessions_7_delta_percentage",
                                    property:
                                        "orgs____sessions_organization__delta_7d_percentage",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_30",
                                    label: "webSessions_30",
                                    property:
                                        "orgs____sessions_organization__count_30d",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_30_delta_percentage",
                                    label: "webSessions_30_delta_percentage",
                                    property:
                                        "orgs____sessions_organization__delta_30d_percentage",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_60",
                                    label: "webSessions_60",
                                    property:
                                        "orgs____sessions_organization__count_60d",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_60_delta_percentage",
                                    label: "webSessions_60_delta_percentage",
                                    property:
                                        "orgs____sessions_organization__delta_60d_percentage",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_90",
                                    label: "webSessions_90",
                                    property:
                                        "orgs____sessions_organization__count_90d",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessions_90_delta_percentage",
                                    label: "webSessions_90_delta_percentage",
                                    property:
                                        "orgs____sessions_organization__delta_90d_percentage",
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "dau",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "dau",
                                    label: "dau",
                                    property: "orgs____active_users__dau",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "dauPercentage",
                                    label: "dauPercentage",
                                    property:
                                        "orgs____active_users__dau_percentage",
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "mau",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "mau",
                                    label: "mau",
                                    property: "orgs____active_users__mau",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "mauPercentage",
                                    label: "mauPercentage",
                                    property:
                                        "orgs____active_users__mau_percentage",
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            key: "stickiness",
                            label: "stickiness",
                            property: "orgs____active_users__stickiness",
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "traitsUserProvided",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/integration-traits", {
                        params: {
                            per_page: 1000,
                            q: [
                                "provider_id[eq]=105",
                                "interaction_type_id[eq]=1",
                            ],
                            traits: "true",
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((integrationTrait) => {
                            items.push({
                                definition: Object.assign({
                                    key: integrationTrait.segment_identifier,
                                    label: () => {
                                        return (
                                            integrationTrait.label ||
                                            integrationTrait.attribute
                                        );
                                    },
                                    property:
                                        "orgs__" +
                                        integrationTrait.segment_identifier,
                                }),
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "traitsCustomFields",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/integration-traits", {
                        params: {
                            per_page: 1000,
                            q: [
                                "provider_id[eq]=134",
                                "interaction_type_id[eq]=1",
                            ],
                            traits: "true",
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((integrationTrait) => {
                            items.push({
                                definition: Object.assign({
                                    key: integrationTrait.segment_identifier,
                                    label: () => {
                                        return (
                                            integrationTrait.label ||
                                            integrationTrait.attribute
                                        );
                                    },
                                    property:
                                        "orgs__" +
                                        integrationTrait.segment_identifier,
                                }),
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "traitsIntegrations",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/integrations", {
                        params: {
                            per_page: 100,
                            q: ["provider_id[notin]=105,131,134"],
                            sort_by: "providers.name:asc",
                            include: ["provider"],
                            traits: "true",
                        },
                    })
                    .then((response) => {
                        var items = [];
                        response.data.data.map((integration) => {
                            items.push({
                                definition: Object.assign({
                                    label: () => {
                                        return (
                                            integration.label ||
                                            integration.provider.name
                                        );
                                    },
                                }),
                                children: axios
                                    .get(
                                        window.akita.urls.api +
                                            "/v1/integration-traits",
                                        {
                                            params: {
                                                per_page: 1000,
                                                q: [
                                                    "provider_id[eq]=" +
                                                        integration.provider_id,
                                                    "interaction_type_id[eq]=1",
                                                ],
                                                sort_by: "label:asc",
                                                traits: "true",
                                            },
                                        }
                                    )
                                    .then(function (response) {
                                        var items = [];
                                        response.data.data.map(
                                            (integrationTrait) => {
                                                items.push({
                                                    definition: Object.assign({
                                                        key: integrationTrait.segment_identifier,
                                                        label: () => {
                                                            return (
                                                                integrationTrait.label ||
                                                                integrationTrait.attribute
                                                            );
                                                        },
                                                        property:
                                                            "orgs__" +
                                                            integrationTrait.segment_identifier,
                                                    }),
                                                });
                                            }
                                        );
                                        return items;
                                    }),
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "interactions",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "interactionDates",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    label: "interactionsDatesEmailThreads",
                                }),
                                children: [
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsFirstSent",
                                            label: "interactionsDatesEmailThreadsFirstSent",
                                            property:
                                                "orgs____email_thread_dates_by_organization__date_first_email_sent",
                                        }),
                                        children: [],
                                    },
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsLastSent",
                                            label: "interactionsDatesEmailThreadsLastSent",
                                            property:
                                                "orgs____email_thread_dates_by_organization__date_last_email_sent",
                                        }),
                                        children: [],
                                    },
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsFirstReceived",
                                            label: "interactionsDatesEmailThreadsFirstReceived",
                                            property:
                                                "orgs____email_thread_dates_by_organization__date_first_email_received",
                                        }),
                                        children: [],
                                    },
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsLastReceived",
                                            label: "interactionsDatesEmailThreadsLastReceived",
                                            property:
                                                "orgs____email_thread_dates_by_organization__date_last_email_received",
                                        }),
                                        children: [],
                                    },
                                ],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentConversation",
                                    label: "interactionsMostRecentConversation",
                                    property:
                                        "orgs____interaction_dates_by_organization__conversations",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentDeal",
                                    label: "interactionsMostRecentDeal",
                                    property:
                                        "orgs____interaction_dates_by_organization__deal",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentInvoice",
                                    label: "interactionsMostRecentInvoice",
                                    property:
                                        "orgs____interaction_dates_by_organization__invoice",
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentTicket",
                                    label: "interactionsMostRecentTicket",
                                    property:
                                        "orgs____interaction_dates_by_organization__ticket",
                                }),
                                children: [],
                            },
                        ],
                    },
                ],
            },
        ];

        let result = await this.resolve();
        result.translate($i18n);
        this.children = result.children;
        this.initialized = true;
    };
}

export default Columns;
